// components/CoachInvoice.tsx
import React from 'react';

export interface CoachInvoiceProps {
    coach: any;
    invoiceNumber: string;
    issueDate: string;
    targetMonthText: string;
    revenueNet: number;
    managementBonus?: number; // 💉 NEU: Der Bonus wird separat empfangen
    clearingTotal: number;
    /** Anzahl der betreuten Mandanten für die Abrechnungszeile */
    supervisedClientCount?: number;
    /** PEPulse M-1 Malus (negativ oder 0) */
    pepulseMalus?: number;
    pepulseMalusRate?: number;
    pepulsePoints?: number;
    pepulseMalusBase?: number;
    pepulseTargetPoints?: number;
    pepulseVacationDays?: number;
    pepulsePrevMonth?: string;
    /** 50/50 Umverteilung aus Team-Malus-Pool (positiv) */
    pepulseRedistributionBonus?: number;
}

const CoachInvoice: React.FC<CoachInvoiceProps> = ({
    coach,
    invoiceNumber,
    issueDate,
    targetMonthText,
    revenueNet,
    managementBonus = 0, // 💉 NEU: Fallback auf 0
    clearingTotal,
    supervisedClientCount,
    pepulseMalus = 0,
    pepulseMalusRate = 0,
    pepulsePoints = 0,
    pepulseMalusBase = 0,
    pepulseTargetPoints = 1000,
    pepulseVacationDays = 0,
    pepulsePrevMonth = '',
    pepulseRedistributionBonus = 0,
}) => {
    // 💉 CHIRURGIE-FIX: Ultimativer Schutz gegen leere/alte Datensätze
    const safeCoach = coach || {};
    
    // --- BERECHNUNG ---
    const isReverseCharge = safeCoach.invoiceReverseCharge === true;
    
    // 💉 CHIRURGIE-FIX: Sichere Konvertierung aller Zahlen, um NaN-Crash zu verhindern
    const safeRevenueNet = Number(revenueNet) || 0;
    const safeManagementBonus = Number(managementBonus) || 0;
    const safeClearingTotal = Number(clearingTotal) || 0;
    const safePepulseMalus = Number(pepulseMalus) || 0;
    const safePepulseRedistrib = Number(pepulseRedistributionBonus) || 0;
    
    const totalNet =
        safeRevenueNet +
        safeManagementBonus +
        safeClearingTotal +
        safePepulseMalus +
        safePepulseRedistrib;
    const taxRate = isReverseCharge ? 0 : 0.19;
    const vatAmount = totalNet * taxRate;
    const totalGross = totalNet + vatAmount;

    const formatCurrency = (val: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(val) || 0);
    const formatPoints = (val: number) => new Intl.NumberFormat('de-DE').format(Math.round(Number(val) || 0));
    const firmName = safeCoach.invoiceCompanyName || safeCoach.name || 'Unbekannter Coach';

    const showPepulseMalus = safePepulseMalus < 0 && pepulseMalusRate > 0;
    const showPepulseRedistrib = safePepulseRedistrib > 0;
    const vacDays = Number(pepulseVacationDays) || 0;
    const targetPts = Number(pepulseTargetPoints) || 1000;

    return (
        <div
            className="invoice-print-container w-full bg-white text-black font-sans relative box-border"
            style={{
                width: '210mm',
                minHeight: '297mm',
                padding: '15mm 20mm',
                boxSizing: 'border-box',
                backgroundColor: '#ffffff',
                color: '#000000',
            }}
        >
            
            {/* HEADER */}
            <div className="flex justify-between items-start border-b-2 border-slate-900 pb-4 mb-6 relative z-10">
                <div className="font-sans w-1/2">
                    {safeCoach.invoiceLogoUrl ? (
                        <img src={`/assets/rechnung/${safeCoach.invoiceLogoUrl}`} alt="Logo" className="max-h-20 max-w-[200px] object-contain mb-3" />
                    ) : (
                        <h1 className="text-2xl font-black mb-1 tracking-tighter text-slate-900" style={{ fontSize: '1.5rem' }}>RECHNUNG</h1>
                    )}
                    <p className="text-slate-500 font-bold tracking-widest uppercase mb-4" style={{ fontSize: '10px' }}>Honorarabrechnung / Services</p>
                    <div className="mt-2 text-sm leading-snug text-black">
                        <p className="text-slate-400 mb-1 uppercase tracking-wider" style={{ fontSize: '10px' }}>Rechnung an:</p>
                        <div>
                            <strong>PreisExpert</strong><br/>
                            Christian Funk<br/>
                            Rotehahnstr. 2<br/>
                            21335 Lüneburg<br/>
                            Deutschland
                        </div>
                    </div>
                </div>
                <div className="text-right text-slate-600 leading-snug font-sans bg-slate-50 p-3 rounded border border-slate-100 w-1/2" style={{ maxWidth: '240px', fontSize: '10px' }}>
                    <p className="font-bold text-black text-xs">{firmName}</p>
                    <p>{safeCoach.invoiceStreet || '---'}</p>
                    <p>{safeCoach.invoiceCity || '---'}</p>
                    <p className="mt-1 mb-2 text-[#E6C299] font-bold">{safeCoach.email || '---'}</p>
                    <div className="grid gap-x-2 gap-y-0.5 text-left border-t border-slate-200 pt-2 mt-2" style={{ gridTemplateColumns: 'auto 1fr' }}>
                        <span className="text-slate-400">Steuernr./USt:</span> <span className="font-mono font-bold text-black">{safeCoach.invoiceTaxId || '---'}</span>
                        <span className="text-slate-400">IBAN:</span> <span className="font-mono font-bold text-black">{safeCoach.invoiceIban || '---'}</span>
                        <span className="text-slate-400">BIC:</span> <span className="font-mono font-bold text-black">{safeCoach.invoiceBic || '---'}</span>
                    </div>
                </div>
            </div>

            {/* TABELLE UND DATEN */}
            <div className="relative z-10 font-sans">
                <div className="flex justify-between items-center mb-4 bg-slate-50 p-3 rounded border border-slate-100 text-sm">
                    <div><span className="text-slate-400 block uppercase tracking-wider" style={{ fontSize: '10px' }}>Datum</span><strong className="font-mono text-black">{issueDate || '---'}</strong></div>
                    <div><span className="text-slate-400 block uppercase tracking-wider" style={{ fontSize: '10px' }}>Abrechnungsmonat</span><strong className="font-mono text-black">{targetMonthText || 'UNBEKANNT'}</strong></div>
                    <div className="text-right"><span className="text-slate-400 block uppercase tracking-wider" style={{ fontSize: '10px' }}>Rechnungs-Nr.</span><strong className="font-mono font-bold text-black">{invoiceNumber || '---'}</strong></div>
                </div>
                
                <table className="w-full text-left border-collapse mt-2 text-black text-sm">
                    <thead>
                        <tr className="border-b-2 border-slate-800 text-slate-900 uppercase tracking-wider" style={{ fontSize: '10px' }}>
                            <th className="py-2 px-1 font-bold w-3/5">Beschreibung / Leistung</th>
                            <th className="py-2 px-1 font-bold text-center w-1/5">MwSt.</th>
                            <th className="py-2 px-1 font-bold text-right w-1/5">Betrag (Netto)</th>
                        </tr>
                    </thead>
                    <tbody className="border-b border-slate-300">
                        
                        {/* Reguläre Provision / Fixgehalt */}
                        {safeRevenueNet !== 0 && (
                            <tr className="border-b border-slate-100">
                                <td className="py-4 px-1">
                                    <strong>PreisExpert Abrechnung</strong><br/>
                                    <span className="text-slate-500" style={{ fontSize: '10px' }}>
                                        Anteil an Einnahmen (Provision & Fixgehalt
                                        {typeof supervisedClientCount === 'number' && !Number.isNaN(supervisedClientCount)
                                            ? ` · ${supervisedClientCount} ${supervisedClientCount === 1 ? 'Mandant' : 'Mandanten'} betreut`
                                            : ''}
                                        )
                                    </span>
                                </td>
                                <td className="py-4 px-1 text-center text-slate-500 font-mono">{isReverseCharge ? '0%' : '19%'}</td>
                                <td className="py-4 px-1 text-right font-mono">{formatCurrency(safeRevenueNet)}</td>
                            </tr>
                        )}

                        {/* 💉 NEU: Eigene Zeile für den Management-Bonus (nur wenn > 0) */}
                        {safeManagementBonus > 0 && (
                            <tr className="border-b border-slate-100">
                                <td className="py-4 px-1">
                                    <strong>Team-Bonus (Management)</strong><br/>
                                    <span className="text-slate-500" style={{ fontSize: '10px' }}>Anteilige Umsatzbeteiligung (Team-Split)</span>
                                </td>
                                <td className="py-4 px-1 text-center text-slate-500 font-mono">{isReverseCharge ? '0%' : '19%'}</td>
                                <td className="py-4 px-1 text-right font-mono">{formatCurrency(safeManagementBonus)}</td>
                            </tr>
                        )}

                        {/* Clearing / Verrechnungen */}
                        {safeClearingTotal !== 0 && (
                            <tr className="border-b border-slate-100">
                                <td className="py-4 px-1">
                                    <strong>Clearing & Verrechnung</strong><br/>
                                    <span className="text-slate-500" style={{ fontSize: '10px' }}>zuzügl. Provisionsabrechnung / abzügl. Kosten</span>
                                </td>
                                <td className="py-4 px-1 text-center text-slate-500 font-mono">{isReverseCharge ? '0%' : '19%'}</td>
                                <td className="py-4 px-1 text-right font-mono">{formatCurrency(safeClearingTotal)}</td>
                            </tr>
                        )}

                        {/* PEPulse M-1 Malus */}
                        {showPepulseMalus && (
                            <tr className="border-b border-slate-100">
                                <td className="py-4 px-1">
                                    <strong>PEPulse Social Media Malus</strong><br/>
                                    <span className="text-slate-500" style={{ fontSize: '10px' }}>
                                        {`Vormonat ${pepulsePrevMonth || '—'} (${formatPoints(pepulsePoints)} / ${formatPoints(targetPts)} Pkt${vacDays > 0 ? `, ${vacDays} Urlaubstage` : ''} = ${pepulseMalusRate}% Abzug von ${formatCurrency(pepulseMalusBase)})`}
                                    </span>
                                </td>
                                <td className="py-4 px-1 text-center text-slate-500 font-mono">{isReverseCharge ? '0%' : '19%'}</td>
                                <td className="py-4 px-1 text-right font-mono text-red-700">{formatCurrency(safePepulseMalus)}</td>
                            </tr>
                        )}

                        {/* PEPulse 50/50 Umverteilung GF */}
                        {showPepulseRedistrib && (
                            <tr className="border-b border-slate-100">
                                <td className="py-4 px-1">
                                    <strong>PEPulse Malus-Umverteilung (50 %)</strong><br/>
                                    <span className="text-slate-500" style={{ fontSize: '10px' }}>
                                        {`Anteil am Team-Malus-Pool Vormonat ${pepulsePrevMonth || '—'} (GF Christian / Martina)`}
                                    </span>
                                </td>
                                <td className="py-4 px-1 text-center text-slate-500 font-mono">{isReverseCharge ? '0%' : '19%'}</td>
                                <td className="py-4 px-1 text-right font-mono text-emerald-700">{formatCurrency(safePepulseRedistrib)}</td>
                            </tr>
                        )}

                        {/* Fallback für leere Belege (historische Leichen) */}
                        {safeRevenueNet === 0 && safeManagementBonus === 0 && safeClearingTotal === 0 && !showPepulseMalus && !showPepulseRedistrib && (
                             <tr className="border-b border-slate-100">
                                <td className="py-4 px-1">
                                    <strong>Historischer Beleg</strong><br/>
                                    <span className="text-slate-500" style={{ fontSize: '10px' }}>Keine detaillierten Beträge hinterlegt</span>
                                </td>
                                <td className="py-4 px-1 text-center text-slate-500 font-mono">-</td>
                                <td className="py-4 px-1 text-right font-mono">{formatCurrency(0)}</td>
                            </tr>
                        )}

                        <tr className="border-b border-slate-100"><td className="py-8"></td><td></td><td></td></tr>
                    </tbody>
                </table>

                {/* SUMMENBLOCK */}
                <div className="flex justify-end mt-3 mb-6 text-black">
                    <table className="w-64 text-sm">
                        <tbody>
                            <tr>
                                <td className="py-1 text-slate-500">Summe (Netto):</td>
                                <td className="py-1 text-right font-mono">{formatCurrency(totalNet)}</td>
                            </tr>
                            <tr>
                                <td className="py-1 text-slate-500 border-b border-slate-200 pb-2">{isReverseCharge ? 'Umsatzsteuer (0%):' : 'Umsatzsteuer (19%):'}</td>
                                <td className="py-1 text-right font-mono border-b border-slate-200 pb-2">{formatCurrency(vatAmount)}</td>
                            </tr>
                            <tr className="text-base font-bold text-black">
                                <td className="py-2">Gesamtsumme:</td>
                                <td className="py-2 text-right font-mono bg-[#7ed957] text-black px-2">{formatCurrency(totalGross)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                
                {/* FOOTER */}
                <div className="bg-slate-50 p-4 rounded border border-slate-200 leading-snug mb-4 text-black" style={{ fontSize: '11px' }}>
                    <h4 className="font-bold text-slate-900 mb-1 uppercase tracking-wider" style={{ fontSize: '9px' }}>Zahlungsinformationen</h4>
                    <p className="mb-1 text-slate-700 font-bold">Bitte überweisen Sie den gesamten Betrag schnellstmöglich auf folgendes Konto:</p>
                    <p className="font-mono text-slate-600 mt-2">IBAN: <strong className="text-black">{safeCoach.invoiceIban || '---'}</strong></p>
                    <p className="font-mono text-slate-600 mt-1">BIC: <strong className="text-black">{safeCoach.invoiceBic || '---'}</strong></p>
                </div>

                <div className="text-slate-500 space-y-2 mt-8" style={{ fontSize: '9px' }}>
                    {isReverseCharge && (
                        <p className="text-amber-700 font-bold border-l-2 border-amber-400 pl-2 py-1 bg-amber-50">
                            Steuerfreie innergemeinschaftliche Leistung / Steuerschuldner ist der Leistungsempfänger (Reverse Charge Verfahren).
                        </p>
                    )}
                    <p className="text-slate-900 font-black uppercase tracking-widest text-xs mt-6">Vielen Dank für Ihren Auftrag!</p>
                </div>
            </div>
        </div>
    );
}
export default CoachInvoice;
